import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Maximize2, Minimize2, X } from "lucide-react";
import { useMutation, useQuery } from "convex/react";
import { chatApi } from "../api/client";
import { clerkFetch } from "../api/clerkFetch";
import { useOnline } from "../hooks/useOnline";
import { chatsStore, useChatsStore } from "../stores/chatsStore";
import type { Chat } from "../types";
import { ChatMessage } from "./ChatMessage";
import { ToolCallCard } from "./ToolCallCard";
import { FileEditCard } from "./FileEditCard";
import { ApprovalCard } from "./ApprovalCard";
import type { ChatEvent } from "../types";
import { api } from "../lib/convexApi";
import { resolveFileRef, type ResolveCtx, type ResolvedRef } from "../utils/fileRefResolve";

interface FileLinkLayerProps {
  ctx: ResolveCtx;
  onOpenFileRef?: (ref: ResolvedRef) => void;
  children: ReactNode;
}

/** Wraps rendered chat HTML: resolves .file-link spans (adds is-resolved) and
 *  opens the resolved file on double-click. Exported for testing. */
export function FileLinkLayer({ ctx, onOpenFileRef, children }: FileLinkLayerProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  // Resolve .file-link spans: scan existing ones, then observe for spans
  // injected by streaming re-renders (avoids re-scanning the whole subtree on
  // every token). Keyed on the ctx PRIMITIVES (not the ctx object, which is a
  // fresh literal each render) so it re-runs when the project changes but not
  // on unrelated re-renders. On a project switch we clear + re-resolve every
  // span: a resolved path is only valid for the ctx that produced it (see
  // keyFor in fileRefResolve), so a stale data-resolved-path would otherwise
  // make double-click open the previous project's file (Codex C1).
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    let cancelled = false;

    const resolveSpan = (span: HTMLElement) => {
      if (span.hasAttribute("data-resolved")) return;
      span.setAttribute("data-resolved", "pending");
      const path = span.getAttribute("data-path") || "";
      const lineAttr = span.getAttribute("data-line");
      const line = lineAttr ? Number(lineAttr) : undefined;
      void resolveFileRef({ raw: path, path, line, start: 0, end: path.length }, ctx).then(
        (res) => {
          if (cancelled) return;
          if (!res) {
            span.setAttribute("data-resolved", "no");
            return;
          }
          // Store the RESOLVED path/line so double-click opens the real file,
          // even when the agent typed a basename or a cwd-relative path.
          span.setAttribute("data-resolved", "yes");
          span.dataset.resolvedPath = res.path;
          if (res.line != null) span.dataset.resolvedLine = String(res.line);
          span.classList.add("is-resolved");
          span.setAttribute(
            "title",
            `Double-click to open ${res.path}${res.line ? ":" + res.line : ""}`
          );
        }
      );
    };

    const scan = (parent: ParentNode) =>
      parent.querySelectorAll<HTMLElement>(".file-link:not([data-resolved])").forEach(resolveSpan);

    // Drop resolution state left by a PRIOR ctx so the :not([data-resolved])
    // scan below re-resolves these spans under the new root.
    root.querySelectorAll<HTMLElement>(".file-link[data-resolved]").forEach((span) => {
      span.removeAttribute("data-resolved");
      delete span.dataset.resolvedPath;
      delete span.dataset.resolvedLine;
      span.classList.remove("is-resolved");
      span.removeAttribute("title");
    });

    scan(root);
    const observer = new MutationObserver((mutations) => {
      for (const mut of mutations) {
        mut.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches?.(".file-link")) resolveSpan(node);
          scan(node);
        });
      }
    });
    observer.observe(root, { childList: true, subtree: true });
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [ctx.projectId, ctx.rootPath]);

  // Delegated double-click -> open the RESOLVED path/line (fall back to the
  // raw data-* attributes if resolution data is somehow absent).
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    function onDbl(e: MouseEvent) {
      const span = (e.target as HTMLElement | null)?.closest?.(
        ".file-link.is-resolved"
      ) as HTMLElement | null;
      if (!span) return;
      e.preventDefault();
      const path = span.dataset.resolvedPath || span.getAttribute("data-path");
      if (!path || !onOpenFileRef) return;
      const lineStr = span.dataset.resolvedLine ?? span.getAttribute("data-line") ?? null;
      onOpenFileRef({ path, line: lineStr != null ? Number(lineStr) : undefined });
    }
    root.addEventListener("dblclick", onDbl);
    return () => root.removeEventListener("dblclick", onDbl);
  }, [onOpenFileRef]);

  // display:contents so this wrapper delegates events + can querySelectorAll
  // the rendered spans WITHOUT generating a layout box (preserves the existing
  // chat-panel__messages flex column).
  return (
    <div ref={ref} style={{ display: "contents" }}>
      {children}
    </div>
  );
}

const SLASH_HINT =
  "Slash commands like `/model` only work inside the CLI's **interactive** mode. From Steerly's chat you talk directly to the model — there's no slash interpreter here. To change models or settings, click **Terminal** in the topbar to open an interactive CLI session, or switch agent in the chat header.";

// Matches CLI-style slash commands (`/model`, `/help`, `/clear gpt-5`).
// Deliberately strict so absolute filesystem paths like `/Users/daniel/...`
// are sent normally — those start with `/` followed by an uppercase letter
// or a known POSIX root name (`Users`, `Applications`, `etc`, `var`, ...).
// CLI slash commands always use lowercase ASCII verbs.
function isSlashCommand(text: string): boolean {
  return /^\/[a-z][a-z0-9-]*(\s|$)/.test(text);
}

interface ChatPanelProps {
  // Which chat this panel renders. In the multi-chat grid every pane passes
  // its own chatId; when omitted the panel falls back to the focused chat
  // (legacy single-panel callers).
  chatId?: string;
  isMaxed?: boolean;
  onEnlarge?: () => void;
  onClose?: () => void;
  onOpenPreview?: (path: string) => void;
  onOpenFileRef?: (ref: ResolvedRef) => void;
  fileRefCtx?: ResolveCtx;
}

export function ChatPanel({
  chatId,
  isMaxed,
  onEnlarge,
  onClose,
  onOpenPreview,
  onOpenFileRef,
  fileRefCtx,
}: ChatPanelProps = {}) {
  const focusedId = useChatsStore((s) => s.activeId);
  const resolvedId = chatId ?? focusedId;
  const active = useChatsStore((s) =>
    resolvedId ? s.chats.find((c) => c.id === resolvedId) || null : null
  );
  // True when this pane is the focused one (drives the right-rail inspector +
  // a subtle active-border on the pane in the grid).
  const isFocused = resolvedId != null && resolvedId === focusedId;
  const pendingApprovals = useChatsStore((s) => s.pendingApprovals);

  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const abortRef = useRef<(() => void) | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Reactive online/offline status — disables the send button while the
  // browser reports no network. Convex would queue the mutation when it
  // came back, but the user experience is clearer if the affordance shows
  // we won't accept input right now. Spec §6.7 (offline indicator).
  const online = useOnline();

  // Spec 5.1 PR 5 — approval resolution goes through Convex; the
  // /api/approvals/:id/decision REST route returns 410. Used by the
  // ApprovalCard onDecision below.
  const resolveApprovalMut = useMutation((api as any).approvals.mutations.resolve);

  // Inline rename: double-click the header title to edit it. Optimistically
  // updates the store, then persists via chats.title (the reactive list
  // reconciles the server-truth title on the next tick).
  const renameMut = useMutation((api as any).chats.mutations.title);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  async function saveTitle() {
    const next = titleDraft.trim();
    setEditingTitle(false);
    if (!active || !next || next === active.title) return;
    chatsStore.upsert({ ...active, title: next });
    try {
      await renameMut({ id: active.id as never, title: next });
    } catch (err) {
      chatsStore.setError(err instanceof Error ? err.message : "Could not rename chat");
    }
  }

  // Precompute the position of each message inside its role-group in a single
  // pass. Memoized on the messages array so it doesn't recompute on every
  // keystroke in the composer (which mutates `draft` and re-renders).
  const groupIndices = useMemo<number[]>(() => {
    const msgs = active?.messages;
    if (!msgs || msgs.length === 0) return [];
    const out: number[] = [];
    let run = 0;
    for (let i = 0; i < msgs.length; i++) {
      const prev = msgs[i - 1];
      if (!prev || prev.role !== msgs[i].role) run = 0;
      else run += 1;
      out.push(run);
    }
    return out;
  }, [active?.messages]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [active?.messages?.length, active?.messages?.[active?.messages?.length - 1]?.content]);

  // Convex reactive history. When the active chat is a Convex-backed id,
  // useQuery subscribes to api.chatMessages.queries.forChat and the
  // effect below merges committed messages into the local store. The
  // "skip" sentinel (per Convex convention) avoids a subscription when
  // there's no active chat. Per [[feedback-pr46-review-patterns]] #5
  // we consume the actual Convex row shape (_id, role, content, isFinal).
  // Convex IDs match /^[a-z0-9_]+$/ with a deterministic prefix; legacy
  // UUIDs hold dashes. Skip the useQuery when the active id is a legacy
  // UUID — passing a UUID through `chatId: v.id("chats")` validation
  // throws and crashes the React render (Gemini PR #58 finding).
  const isLikelyConvexId = (id: string | null): id is string =>
    typeof id === "string" && id.length > 0 && !id.includes("-");
  const convexMessages = useQuery(
    api.chatMessages.queries.forChat,
    resolvedId && isLikelyConvexId(resolvedId) ? { chatId: resolvedId as any } : "skip"
  );
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Spec 5.1 PR 6 — chat metadata (title, agentName, etc.) is now
  // reactive via Convex. Per [[feedback-spec5-pr54-64-review-patterns]]
  // #16, only call the byId query when the active id parses as a
  // Convex id; legacy `chat-…` UUIDs would trip v.id() validation.
  const convexChat = useQuery(
    api.chats.queries.byId,
    resolvedId && isLikelyConvexId(resolvedId) ? { id: resolvedId as any } : "skip"
  );
  useEffect(() => {
    if (!mountedRef.current) return;
    if (!convexChat) return;
    const existing = chatsStore.getSnapshot().chats.find((c) => c.id === (convexChat as any)._id);
    // Merge Convex metadata into the local Chat row while preserving
    // the locally-held messages/approvals/resumeHandle which Convex's
    // chats table does not yet own (those live on chatMessages +
    // approvals + per-driver session state). Per
    // [[feedback-spec5-pr54-64-review-patterns]] #14 — never clobber
    // local optimistic state on a reactive snapshot.
    const merged: Chat = {
      id: (convexChat as any)._id,
      workbenchId: ((convexChat as any).workbenchId as string) || existing?.workbenchId || "",
      agent: ((convexChat as any).agentName as Chat["agent"]) || existing?.agent || "claude",
      workspaceDir:
        ((convexChat as any).workspaceDir as string) || existing?.workspaceDir || "/tmp",
      title: ((convexChat as any).title as string) || existing?.title || "Untitled chat",
      createdAt: ((convexChat as any).createdAt as number) ?? existing?.createdAt ?? 0,
      lastActivity:
        ((convexChat as any).lastMessageAt as number) ??
        ((convexChat as any).createdAt as number) ??
        existing?.lastActivity ??
        0,
      messages: existing?.messages ?? [],
      approvals: existing?.approvals ?? [],
      // The Convex chats schema stores resumeHandle as v.optional(v.string())
      // but the local Chat type uses {kind, value} | null. The local store is
      // the source of truth for this field until the schema migration lands —
      // preserve the existing value and ignore what Convex returns for now.
      resumeHandle: existing?.resumeHandle ?? null,
    };
    chatsStore.upsert(merged);
  }, [convexChat]);

  useEffect(() => {
    if (!resolvedId) return;
    if (!mountedRef.current) return;
    if (convexMessages === undefined) {
      // Convex still resolving — leave the local store alone until
      // the reactive query lands. Spec 5.1 PR 6 dropped the legacy
      // chatApi.get fallback because /api/chats/:id is 410 from this
      // PR forward; convexChat (byId) above covers metadata.
      return;
    }
    // Convex resolved. Merge committed messages into the local store
    // without stomping in-flight SSE optimistic messages OR per-message
    // tool-call/file-edit metadata that's not yet persisted in the
    // Convex chatMessages table (Gemini PR #58 finding — the prior
    // version replaced `messages` wholesale + hardcoded toolCalls/
    // fileEdits to []).
    //
    // Merge rules:
    //   1. Convex-committed messages are keyed on a stable (role, ts)
    //      tuple if no Convex _id collision in local — preserve the
    //      local store's toolCalls/fileEdits if a local row matches.
    //   2. Local-only messages (optimistic SSE not yet committed,
    //      identified by ts > newest convex createdAt) stay in the
    //      tail untouched.
    //   3. If Convex returned nothing, leave the local store alone
    //      (cold-start race window).
    if (Array.isArray(convexMessages) && convexMessages.length > 0) {
      const existing = chatsStore.getSnapshot().chats.find((c) => c.id === resolvedId);
      if (!existing) return;
      const localByTs = new Map<number, any>();
      for (const m of (existing as any).messages ?? []) {
        if (typeof m?.ts === "number") localByTs.set(m.ts, m);
      }
      const convexCreatedAts = (convexMessages as any[]).map((m) => m.createdAt || 0);
      const newestConvexTs = Math.max(0, ...convexCreatedAts);
      const merged = (convexMessages as any[]).map((m, i) => {
        const local = localByTs.get(m.createdAt || 0);
        return {
          // Carry a stable id so React keys (key={m.id}) and id-based matching
          // (mm.id === m.id, line ~547) stay correct across reactive ticks.
          // Without it every committed message merges with id=undefined →
          // key collisions and message-level state (tool-call/file-edit cards,
          // the streaming flag) sticking to the wrong row.
          id: m._id ?? local?.id ?? `cm-${m.createdAt || 0}-${i}`,
          role: m.role,
          content: m.content || "",
          // Preserve tool calls + file edits from the local optimistic row
          // (they aren't persisted in chatMessages yet) instead of dropping
          // them on every reactive update.
          toolCalls: local?.toolCalls ?? [],
          fileEdits: local?.fileEdits ?? [],
          ts: m.createdAt || Date.now(),
        };
      });
      // Tail-append any local-only rows newer than the freshest Convex
      // row — these are optimistic SSE messages mid-stream.
      const optimisticTail = ((existing as any).messages ?? []).filter(
        (m: any) => typeof m?.ts === "number" && m.ts > newestConvexTs
      );
      chatsStore.upsert({ ...existing, messages: [...merged, ...optimisticTail] } as any);
    }
  }, [resolvedId, convexMessages]);

  // Cleanup in-flight send when active chat changes
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current();
        abortRef.current = null;
      }
    };
  }, [resolvedId]);

  function handleEvent(e: ChatEvent) {
    if (!resolvedId) return;
    if (e.type === "delta" && e.content) {
      chatsStore.appendDelta(resolvedId, e.content);
    } else if (e.type === "approval-needed" && e.approvalRequest) {
      chatsStore.pushApproval(resolvedId, e.approvalRequest);
    } else if (e.type === "final") {
      setSending(false);
    } else if (e.type === "error") {
      chatsStore.setError(e.errorMessage || "stream error");
      setSending(false);
    }
  }

  function handleSend(event: React.FormEvent) {
    event.preventDefault();
    const text = draft.trim();
    // G69-1 / C69-3: the Send BUTTON respects `online`, but the Enter
    // hotkey path also calls handleSend directly — without this guard
    // pressing Enter while offline bypassed the disabled state and queued
    // a send that would silently fail. Mirror the button's gate here so
    // both input paths behave identically.
    if (!text || !active || sending || !online) return;
    if (isSlashCommand(text)) {
      setDraft("");
      chatsStore.appendMessage(active.id, {
        id: `msg-local-${Date.now()}-user`,
        role: "user",
        content: text,
        ts: Date.now(),
        toolCalls: [],
        fileEdits: [],
      });
      chatsStore.appendMessage(active.id, {
        id: `msg-local-${Date.now()}-hint`,
        role: "assistant",
        content: SLASH_HINT,
        ts: Date.now() + 1,
        toolCalls: [],
        fileEdits: [],
      });
      return;
    }
    setDraft("");
    setSending(true);
    // Optimistic local echo of user message
    chatsStore.appendMessage(active.id, {
      id: `msg-local-${Date.now()}`,
      role: "user",
      content: text,
      ts: Date.now(),
      toolCalls: [],
      fileEdits: [],
    });
    abortRef.current = chatApi.sendMessage(active.id, text, handleEvent, (err) => {
      chatsStore.setError(err.message);
      setSending(false);
    });
  }

  if (!active) {
    return (
      <div className="chat-panel chat-panel--empty">
        <p>Pick a chat from the left, or start a new one with the + button.</p>
      </div>
    );
  }

  return (
    <div
      className={`chat-panel${isFocused ? " chat-panel--focused" : ""}`}
      onMouseDown={() => {
        // Clicking anywhere in a pane focuses it (drives the inspector). Cheap
        // no-op if already focused.
        if (resolvedId && resolvedId !== focusedId) chatsStore.setActive(resolvedId);
      }}
    >
      <header className="chat-panel__header">
        {editingTitle ? (
          <input
            className="chat-panel__title-input"
            autoFocus
            value={titleDraft}
            aria-label="Chat title"
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={() => void saveTitle()}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void saveTitle();
              } else if (e.key === "Escape") {
                e.preventDefault();
                setEditingTitle(false);
              }
            }}
          />
        ) : (
          <span
            className="chat-panel__title"
            title="Double-click to rename"
            onDoubleClick={() => {
              setTitleDraft(active.title);
              setEditingTitle(true);
            }}
          >
            {active.title}
          </span>
        )}
        <span className="chat-panel__agent">{active.agent}</span>
        <div className="chat-panel__header-actions">
          {onEnlarge && (
            <button
              type="button"
              className="chat-panel__header-btn"
              aria-label={isMaxed ? "Restore chat size" : "Enlarge chat"}
              title={isMaxed ? "Restore" : "Enlarge"}
              onClick={onEnlarge}
            >
              {isMaxed ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
          )}
          {onClose && (
            <button
              type="button"
              className="chat-panel__header-btn"
              aria-label="Close chat pane"
              title="Close"
              onClick={onClose}
            >
              <X size={14} />
            </button>
          )}
        </div>
      </header>
      <div ref={scrollRef} className="chat-panel__messages">
        <FileLinkLayer
          ctx={fileRefCtx ?? { projectId: null, rootPath: null }}
          onOpenFileRef={onOpenFileRef}
        >
          {active.messages.map((m, i) => {
            const groupIndex = groupIndices[i] ?? 0;
            const isFirstOfGroup = groupIndex === 0;
            const showDivider = groupIndex > 0 && groupIndex % 6 === 0;
            return (
              <div key={m.id}>
                {isFirstOfGroup && (
                  <div
                    data-testid="chat-role-label"
                    className={`chat-role-label chat-role-label--${m.role === "user" ? "user" : "agent"}`}
                  >
                    {m.role === "user" ? "You" : m.role === "tool" ? "Tool" : "Assistant"}
                  </div>
                )}
                {showDivider && <hr className="chat-group__divider" data-testid="chat-divider" />}
                <ChatMessage
                  role={m.role}
                  content={m.content}
                  streaming={sending && i === active.messages.length - 1 && m.role === "assistant"}
                  showLabel={false}
                  fileLinks
                />
                {m.toolCalls?.map((c) => (
                  <ToolCallCard key={c.id} call={c} />
                ))}
                {m.fileEdits?.map((f) => (
                  <FileEditCard
                    key={`${f.path}-${f.ts}`}
                    edit={f}
                    onApply={async () => {
                      try {
                        const res = await clerkFetch("/api/fs/write", {
                          method: "POST",
                          headers: { "content-type": "application/json" },
                          credentials: "same-origin",
                          body: JSON.stringify({
                            path: f.path,
                            content: f.diff,
                            chatId: active.id,
                          }),
                        });
                        if (!res.ok) throw new Error(`Apply failed: ${res.status}`);
                        chatsStore.upsert({
                          ...active,
                          messages: active.messages.map((mm) =>
                            mm.id === m.id
                              ? {
                                  ...mm,
                                  fileEdits: (mm.fileEdits || []).map((ee) =>
                                    ee.path === f.path && ee.ts === f.ts
                                      ? { ...ee, applied: true }
                                      : ee
                                  ),
                                }
                              : mm
                          ),
                        });
                      } catch (err) {
                        chatsStore.setError(err instanceof Error ? err.message : "Apply failed");
                      }
                    }}
                    onReject={() => {
                      chatsStore.upsert({
                        ...active,
                        messages: active.messages.map((mm) =>
                          mm.id === m.id
                            ? {
                                ...mm,
                                fileEdits: (mm.fileEdits || []).filter(
                                  (ee) => !(ee.path === f.path && ee.ts === f.ts)
                                ),
                              }
                            : mm
                        ),
                      });
                    }}
                    onOpenPreview={(path) => {
                      if (onOpenPreview) {
                        onOpenPreview(path);
                      } else {
                        console.warn("Open Preview not yet wired to file editor:", path);
                      }
                    }}
                  />
                ))}
              </div>
            );
          })}
        </FileLinkLayer>
        {(pendingApprovals.get(active.id) || []).map((req) => (
          <ApprovalCard
            key={req.id}
            request={req}
            onDecision={async (decision, scope) => {
              // Spec 5.1 PR 5 — /api/approvals/:id/decision migrated to
              // Convex. Map the legacy REST decision strings
              // ("approved"/"denied") to the Convex enum
              // ("allowed"/"denied"/"cancelled"). Per
              // [[feedback-spec5-pr54-64-review-patterns]] #16, validate
              // the id is a Convex id before passing through useMutation —
              // legacy ids (UUIDs / "appr-…") would throw on v.id()
              // validation. When the id isn't Convex-shaped we still
              // update the local store so the UI clears; the underlying
              // approval row was likely served by a stale legacy stream
              // and is no longer write-targettable.
              const convexDecision: "allowed" | "denied" | "cancelled" =
                decision === "approved" ? "allowed" : "denied";
              // G75-7 / Per [[feedback-spec5-pr54-64-review-patterns]] #16 —
              // guard any non-Convex id (legacy `appr-` AND UUIDs). Real
              // Convex ids never contain dashes.
              const isConvexId = typeof req.id === "string" && !req.id.includes("-");
              // C75-7: only clear the local approval card AFTER the
              // backend decision succeeds. Previously the card was
              // wiped even on a 403/network error, leaving the row
              // pending in storage while the UI showed it as resolved.
              if (isConvexId) {
                try {
                  await resolveApprovalMut({
                    id: req.id as never,
                    decision: convexDecision,
                    reason: `Chat scope: ${scope}`,
                  });
                  chatsStore.resolveApproval(active.id, req.id, decision, scope);
                } catch (err) {
                  console.warn("[approvals] resolve failed", err);
                  // Surface the failure so the user sees the card stays
                  // pending and can retry.
                  return;
                }
              } else {
                // Legacy `appr-*` (JsonStore) IDs — Convex mutation
                // can't decide these. The pre-410 REST decision route
                // is still live (Spec 5.1 PR5 C75-6 restored it for
                // exactly this case); POST to it so JsonStore-backed
                // approvals from firewall preflight can still be
                // decided through chat. C75-9: route is gated on
                // base|security plan, so Base-tier users can still
                // act on approvals surfaced in chat.
                try {
                  await fetch(`/api/approvals/${encodeURIComponent(req.id)}/decision`, {
                    method: "POST",
                    headers: { "content-type": "application/json" },
                    body: JSON.stringify({
                      decision: decision === "approved" ? "approved" : "denied",
                      note: `Chat scope: ${scope}`,
                    }),
                    credentials: "include",
                  });
                  chatsStore.resolveApproval(active.id, req.id, decision, scope);
                } catch (err) {
                  console.warn("[approvals] legacy decision failed", err);
                  return;
                }
              }
            }}
          />
        ))}
      </div>
      {active.usage && (active.usage.in > 0 || active.usage.out > 0) && (
        <div className="chat-panel__usage">
          Tokens: <strong>{active.usage.in.toLocaleString()}</strong> in /&nbsp;
          <strong>{active.usage.out.toLocaleString()}</strong> out
        </div>
      )}
      <form onSubmit={handleSend} className="composer">
        <div className="composer__chips">
          <span className="composer__chip">{active.agent}</span>
          {active.title && (
            <span className="composer__chip composer__chip--muted">· {active.title}</span>
          )}
        </div>
        <textarea
          className="composer__input"
          placeholder="Type a message…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend(e as unknown as React.FormEvent);
            }
          }}
          disabled={sending}
        />
        <div className="composer__actions">
          <button
            type="button"
            className="composer__overflow"
            aria-label="More composer options"
            onClick={() => setOverflowOpen((v) => !v)}
          >
            ⋯
          </button>
          <button
            type="submit"
            className="composer__send"
            disabled={!draft.trim() || sending || !online}
            title={online ? undefined : "Offline — reconnecting"}
          >
            {online ? "Send" : "Offline"}
          </button>
        </div>
        {overflowOpen && (
          <div className="composer__overflow-popover">
            {/* Secondary controls placeholder — no extra controls in existing composer */}
          </div>
        )}
      </form>
    </div>
  );
}
